import React, { useState } from 'react';
import { Button, List, Space, Tag, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';

const LABELS = { loader: 'Loader vehicle', walkway: 'Walkway crew', salting: 'Salting route' };
const NOUNS = { loader: 'vehicle', walkway: 'crew', salting: 'route' };

export const assignmentKey = (row) => {
  const target = row.remove_target || {};
  return `${target.setup_id}-${row.kind}-${target.target_id ?? row.entity_id}-${target.target_source ?? 'live'}`;
};

const liveReference = (row) => (row.assignment_id ? `row #${row.assignment_id}` : `vehicle #${row.entity_id}`);

function RemovalConfirmation({ row, setup, busy, onConfirm, onCancel }) {
  const setupName = setup.setup_name || `Setup ${setup.setup_id}`;
  return <div role="group" aria-label={`Confirm removing ${row.name}`} style={{ marginTop: 4 }}>
    <Typography.Paragraph style={{ marginBottom: 4 }}>
      Remove from {row.name} in <em>{setupName}</em>?
    </Typography.Paragraph>
    <Typography.Paragraph style={{ marginBottom: 4 }}>
      This removal takes effect immediately. Canceling staff edits will not restore it.
    </Typography.Paragraph>
    {setup.is_active && <Typography.Paragraph style={{ marginBottom: 4 }}>
      This is the active setup: live allocations update immediately, and unsaved dispatch boards for
      this {NOUNS[row.kind]} are deleted. They're recreated the next time dispatch boards are generated.
      Saved boards are unchanged.
    </Typography.Paragraph>}
    {row.finalized && <Typography.Paragraph style={{ marginBottom: 4 }}>
      This assignment is finalized. Removing it won't unlock the section.
    </Typography.Paragraph>}
    <Space>
      {/* Same danger styling as the staff page's Archive button. */}
      <Button size="small" danger type="primary" icon={<DeleteOutlined />} loading={busy} disabled={busy}
        onClick={onConfirm}>
        Yes, remove
      </Button>
      <Button size="small" disabled={busy} onClick={onCancel}>Cancel</Button>
    </Space>
  </div>;
}

export default function AssignmentList({
  setups = [], live = [], renderSetupExtra, emptyText,
  canRemove = false, onRemove, removalBusy = false, pendingKey = null,
}) {
  const [confirmingKey, setConfirmingKey] = useState(null);
  const removable = canRemove && typeof onRemove === 'function';

  const confirm = async (row, setup) => {
    try {
      await onRemove(row, setup);
    } finally {
      // A changed allocation must be reviewed and confirmed again.
      setConfirmingKey(null);
    }
  };

  return <Space direction="vertical" style={{ width: '100%' }}>
    {(setups.length > 0 || emptyText) && <List size="small" bordered dataSource={setups}
      locale={{ emptyText }} renderItem={(setup) => (
      <List.Item key={setup.setup_id}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space wrap>
            <Typography.Text strong>{setup.setup_name || `Setup ${setup.setup_id}`}</Typography.Text>
            {setup.status && <Tag>{setup.status}</Tag>}
            {setup.is_active && <Tag color="red">Active</Tag>}
          </Space>
          {setup.assignments?.length ? setup.assignments.map((row, index) => {
            const key = assignmentKey(row);
            return <div key={`${key}-${index}`}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span>
                  {LABELS[row.kind]}: {row.name}{' '}
                  {row.finalized && <Tag>Finalized</Tag>}
                </span>
                {removable && row.remove_target && confirmingKey !== key && <Button
                  size="small" danger icon={<DeleteOutlined />} disabled={removalBusy}
                  aria-label={`Remove ${LABELS[row.kind]} ${row.name} from ${setup.setup_name || `Setup ${setup.setup_id}`}`}
                  onClick={() => setConfirmingKey(key)}>Remove</Button>}
              </div>
              {removable && confirmingKey === key && <RemovalConfirmation
                row={row} setup={setup} busy={removalBusy && pendingKey === key}
                onConfirm={() => confirm(row, setup)} onCancel={() => setConfirmingKey(null)} />}
            </div>;
          }) : <Space wrap>{(setup.roles || []).map(role => <Tag key={role}>{role}</Tag>)}</Space>}
          {renderSetupExtra?.(setup)}
        </Space>
      </List.Item>
    )} />}
    {live.length > 0 && <div>
      <Typography.Text strong>Unmatched live assignments</Typography.Text>
      <p>These live assignments are not represented in the active setup. An administrator must remove them in Django admin before this position can be changed.</p>
      <ul>{live.map((row, index) => <li key={`${row.kind}-${row.entity_id}-${index}`}>
        {LABELS[row.kind]}: {row.name}{row.admin_hint ? ` (${row.admin_hint}, ${liveReference(row)})` : ''}
      </li>)}</ul>
    </div>}
  </Space>;
}
